import React from "react";
import { Card, Button, Tag, Dropdown, Avatar } from "antd";
import {
    UserOutlined,
    CheckOutlined,
    StopOutlined,
    DeleteOutlined,
    MoreOutlined,
    MailOutlined,
    PhoneOutlined,
    BuildOutlined,
} from "@ant-design/icons";
import { cn } from "../../../../utils/cn";

const EnhanceUserCardView = ({
    record,
    actions = [],
    testAccount = false,
    SOURCES = {},
    getInitials,
    onCardClick,
}) => {
    // Filter actions based on conditions
    const filteredActions = actions
        .map((action) => {
            // If action has a condition function, check if it should be shown
            if (action.condition && !action.condition(record)) {
                return null;
            }

            // If action has a transform function, use it to modify action properties
            if (action.transform) {
                return action.transform(record, action);
            }

            return action;
        })
        .filter(Boolean); // Remove null actions

    // Create menu items for dropdown
    // const menuItems = filteredActions.map((action, index) => ({
    //   key: index,
    //   label: action.label,
    //   icon: action.icon,
    //   className: action.className,
    //   onClick: () => action.onClick(record),
    // }));

    // Badge color mapping - matching your table exactly
    const getBadgeColor = (badgeData) => {
        if (!badgeData) return "default";
        const colorMap = {
            success: "success",
            danger: "error",
            primary: "processing",
            warning: "warning",
        };
        return colorMap[badgeData.color] || "default";
    };

    // Avatar colors
    // const colors = [
    //   "#1890ff",
    //   "#52c41a",
    //   "#faad14",
    //   "#f5222d",
    //   "#722ed1",
    //   "#13c2c2",
    //   "#eb2f96",
    //   "#fa8c16",
    //   "#a0d911",
    //   "#2f54eb",
    // ];
    const initials = getInitials(record.name);
    // const colorIndex = record.name
    //   ? record.name.charCodeAt(0) % colors.length
    //   : 0;
    // const avatarBgColor = colors[colorIndex];

    return (
        <Card
            hoverable
            className="h-full"
            //   extra={
            //     availableActions.length > 0 ? (
            //       <Dropdown
            //         menu={{ items: menuItems }}
            //         placement="bottomRight"
            //         trigger={["click"]}
            //       >
            //         <Button type="text" icon={<MoreOutlined />} />
            //       </Dropdown>
            //     ) : null
            //   }
            onClick={() => onCardClick(record)}
        >
            <div className="space-y-3 sm:space-y-4">
                {/* User Header */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                        <Avatar
                            size={40}
                            className="flex-shrink-0 font-semibold text-base sm:text-lg !bg-primary sm:w-12 sm:h-12"
                        >
                            {initials}
                        </Avatar>
                        <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-sm sm:text-base mb-1 truncate">
                                {record.name || "N/A"}
                            </h3>
                            {record.company && (
                                <p className="text-gray-500 text-xs sm:text-sm mb-0 truncate">
                                    {record.company}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-1.5 sm:space-y-2 border-b border-gray-200 pb-2 sm:pb-3">
                    {record.email && (
                        <div className="flex items-center text-xs sm:text-sm text-gray-600">
                            <MailOutlined className="mr-2 flex-shrink-0" />
                            <span className="truncate">{record.email}</span>
                        </div>
                    )}
                    {/* {record.phone && ( */}
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <PhoneOutlined className="mr-2 flex-shrink-0" />
                        <span>{record.phone}</span>
                    </div>
                    {/* )} */}
                </div>

                {/* Badges Section - Matching your table exactly */}
                <div className="space-y-2 sm:space-y-3">
                    {/* Verified Badge */}
                    {record.verifiedBadge && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs sm:text-sm text-gray-600 font-medium">
                                Verified:
                            </span>
                            <Tag color={getBadgeColor(record.verifiedBadge)}>
                                {record.verifiedBadge.text}
                            </Tag>
                        </div>
                    )}

                    {/* Plan Badge */}
                    {record.plan && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs sm:text-sm text-gray-600 font-medium">Plan:</span>
                            <Tag color="processing">{record.plan}</Tag>
                        </div>
                    )}

                    {/* Trial Badge */}
                    {record.trialBadge && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs sm:text-sm text-gray-600 font-medium">Trial:</span>
                            <Tag color={getBadgeColor(record.trialBadge)}>
                                {record.trialBadge.text}
                            </Tag>
                        </div>
                    )}

                    {/* Status Badge */}
                    {record.statusBadge && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs sm:text-sm text-gray-600 font-medium">Status:</span>
                            <Tag color={getBadgeColor(record.statusBadge)}>
                                {record.statusBadge.text}
                            </Tag>
                        </div>
                    )}
                </div>

                {/* Action Buttons - Matching your table exactly */}
                <div
                    className={cn(
                        "grid xs:grid-cols-2 grid-cols-1 gap-1.5 sm:gap-2 pt-2 sm:pt-3 border-t border-gray-200",
                        filteredActions.length === 1 && "flex justify-end"
                    )}
                >
                    {filteredActions.map((action, idx) => (
                        <>
                            <Button
                                title={action.title ?? action.label}
                                className={cn(
                                    "text-xs sm:text-sm font-medium h-8 sm:h-9 my-1 !border-primary !text-primary hover:!bg-primary hover:!text-white !min-w-full flex justify-center items-center transition-colors duration-150 ease-in-out",
                                    action.className,
                                    filteredActions.length === 1 && "!w-1/2"
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick(record);
                                }}
                            >
                                {action.icon &&
                                    React.cloneElement(action.icon, { className: "mr-2" })}
                                {action.label}
                            </Button>
                        </>
                    ))}
                </div>
            </div>
        </Card>
    );
};

export default EnhanceUserCardView;
